import React, { useState, useEffect } from 'react';
import RichTextContent from '../../components/RichTextContent';
import { Plus, Trash2, Loader2, Save, ExternalLink, X, Pin, Upload } from 'lucide-react';
import { meshNewsApi, brandsApi, uploadApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import ImageUpload from '../../components/admin/ImageUpload';
import RichTextEditor from '../../components/admin/RichTextEditor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const categories = ['News', 'Product Launch', 'Project', 'Event', 'Award'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [newsRes, brandsRes] = await Promise.all([
        meshNewsApi.getAll(),
        brandsApi.getAll()
      ]);
      setNews(newsRes.data);
      setBrands(brandsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingItem) {
        await meshNewsApi.update(editingItem.id, formData);
      } else {
        await meshNewsApi.create(formData);
      }
      await fetchData();
      setShowModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving news:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) return;
    try {
      await meshNewsApi.delete(id);
      await fetchData();
    } catch (error) {
      console.error('Error deleting news:', error);
    }
  };

  const handleTogglePin = async (item) => {
    try {
      await meshNewsApi.update(item.id, { pinned: !item.pinned });
      await fetchData();
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : brandId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div data-testid="admin-news-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Mesh News</h1>
        <Button onClick={() => { setEditingItem(null); setShowModal(true); }} data-testid="add-news-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add News Article
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm divide-y">
        {news.map((item) => (
          <div key={item.id} className="p-4 flex items-center">
            {item.image && (
              <img
                src={item.image}
                alt={item.title}
                className="w-24 h-16 object-cover rounded mr-4"
              />
            )}
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-2 mb-1">
                {item.pinned && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium flex items-center">
                    <Pin className="h-3 w-3 mr-1" />
                    Pinned
                  </span>
                )}
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                  {item.category}
                </span>
                {item.brandTags?.map(brandId => (
                  <span key={brandId} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">
                    {getBrandName(brandId)}
                  </span>
                ))}
                {item.link && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    {item.linkText || 'Link'}
                  </span>
                )}
                {item.additionalImages?.length > 0 && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">
                    +{item.additionalImages.length} images
                  </span>
                )}
                <span className="text-xs text-gray-500">{item.date}</span>
              </div>
              <h3 className="font-medium text-gray-900">{item.title}</h3>
              <RichTextContent preview content={item.excerpt} className="text-sm text-gray-600 line-clamp-1 [&_*]:my-0 [&_p]:text-inherit [&_*]:text-inherit" testId={`admin-news-excerpt-${item.id}`} />
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant={item.pinned ? "default" : "outline"}
                size="sm"
                onClick={() => handleTogglePin(item)}
                title={item.pinned ? "Unpin article" : "Pin article"}
              >
                <Pin className={`h-4 w-4 ${item.pinned ? 'text-white' : 'text-amber-500'}`} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setEditingItem(item); setShowModal(true); }}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(item.id)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
        {!news.length && (
          <p className="text-gray-500 text-center py-12">No news articles yet</p>
        )}
      </div>

      <NewsModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditingItem(null); }}
        item={editingItem}
        onSave={handleSave}
        saving={saving}
        categories={categories}
        brands={brands}
      />
    </div>
  );
};

const NewsModal = ({ open, onClose, item, onSave, saving, categories, brands }) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    excerpt: '',
    image: '',
    category: 'News',
    link: '',
    linkText: '',
    brandTags: [],
    additionalImages: [],
    pinned: false,
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const additionalFileRef = React.useRef(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        date: item.date || '',
        excerpt: item.excerpt || '',
        image: item.image || '',
        category: item.category || 'News',
        link: item.link || '',
        linkText: item.linkText || '',
        brandTags: item.brandTags || [],
        additionalImages: item.additionalImages || [],
        pinned: item.pinned || false,
      });
    } else {
      setFormData({
        title: '',
        date: '',
        excerpt: '',
        image: '',
        category: 'News',
        link: '',
        linkText: '',
        brandTags: [],
        additionalImages: [],
        pinned: false,
      });
    }
    setNewImageUrl('');
  }, [item, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const toggleBrandTag = (brandId) => {
    setFormData(prev => ({
      ...prev,
      brandTags: prev.brandTags.includes(brandId)
        ? prev.brandTags.filter(id => id !== brandId)
        : [...prev.brandTags, brandId]
    }));
  };

  const removeBrandTag = (brandId) => {
    setFormData(prev => ({
      ...prev,
      brandTags: prev.brandTags.filter(id => id !== brandId)
    }));
  };

  const addAdditionalImage = () => {
    if (newImageUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        additionalImages: [...prev.additionalImages, newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  const removeAdditionalImage = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index)
    }));
  };

  const handleAdditionalFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploadingAdditional(true);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const uploaded = [];
      for (const file of files) {
        const response = await uploadApi.upload(file);
        if (response.data.success) {
          uploaded.push(`${baseUrl}${response.data.url}`);
        }
      }
      if (uploaded.length > 0) {
        setFormData(prev => ({
          ...prev,
          additionalImages: [...prev.additionalImages, ...uploaded]
        }));
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploadingAdditional(false);
      if (additionalFileRef.current) {
        additionalFileRef.current.value = '';
      }
    }
  };

  const openMediaPicker = async () => {
    setShowMediaPicker(true);
    setSelectedMedia([]);
    setLoadingMedia(true);
    try {
      const res = await uploadApi.list();
      setMediaFiles(res.data.files || []);
    } catch (err) {
      console.error('Error loading media:', err);
    } finally {
      setLoadingMedia(false);
    }
  };

  const toggleMediaSelection = (url) => {
    setSelectedMedia(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    );
  };

  const confirmMediaSelection = () => {
    if (selectedMedia.length > 0) {
      setFormData(prev => ({
        ...prev,
        additionalImages: [...prev.additionalImages, ...selectedMedia]
      }));
    }
    setShowMediaPicker(false);
    setSelectedMedia([]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit News Article' : 'Add News Article'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Pin Toggle */}
          <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
            <div>
              <Label className="text-amber-900">Pin Article</Label>
              <p className="text-xs text-amber-700">Pinned articles appear first in the news list</p>
            </div>
            <Switch
              checked={formData.pinned}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, pinned: checked }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                value={formData.date}
                onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                placeholder="e.g., January 15, 2025"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Brand Tags Section */}
          <div className="space-y-2">
            <Label>Brand Tags</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.brandTags.map(brandId => {
                const brand = brands.find(b => b.id === brandId);
                return (
                  <span
                    key={brandId}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                  >
                    {brand?.name || brandId}
                    <button
                      type="button"
                      onClick={() => removeBrandTag(brandId)}
                      className="ml-2 hover:text-blue-900"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
            <Select
              value=""
              onValueChange={(value) => {
                if (value && !formData.brandTags.includes(value)) {
                  toggleBrandTag(value);
                }
              }}
            >
              <SelectTrigger data-testid="brand-tags-select">
                <SelectValue placeholder="Select brands to tag..." />
              </SelectTrigger>
              <SelectContent>
                {brands.filter(b => !formData.brandTags.includes(b.id)).map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>
                    {brand.name} ({brand.country})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Tagged brands will show this news article in their brand page News section.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="excerpt">Article Content</Label>
            <RichTextEditor
              id="excerpt"
              rows={5}
              value={formData.excerpt}
              onChange={(value) => setFormData((prev) => ({ ...prev, excerpt: value }))}
              placeholder="Write the article content..."
            />
          </div>

          <div className="space-y-2">
            <ImageUpload
              label="Main Image"
              value={formData.image}
              onChange={(url) => setFormData((prev) => ({ ...prev, image: url }))}
              previewHeight="h-32"
            />
          </div>

          {/* Additional Images Section */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Additional Images (Optional)</h4>
            
            {formData.additionalImages?.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-3">
                {formData.additionalImages.map((url, index) => (
                  <div key={url} className="relative group">
                    <img src={url} alt={`Additional ${index + 1}`} className="h-20 w-full object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => removeAdditionalImage(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    {url.includes('/uploads/') && (
                      <span className="absolute bottom-1 left-1 text-[10px] bg-green-500 text-white px-1 rounded">
                        Uploaded
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex gap-2">
              <Input
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={addAdditionalImage} disabled={!newImageUrl.trim()}>
                <Plus className="h-4 w-4 mr-1" />
                Add URL
              </Button>
              <input
                ref={additionalFileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleAdditionalFileUpload}
                className="hidden"
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => additionalFileRef.current?.click()}
                disabled={uploadingAdditional}
              >
                {uploadingAdditional ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <><Upload className="h-4 w-4 mr-1" />Upload Images</>
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Add more images to display in the article gallery. Select multiple files at once or add URLs individually.
            </p>
          </div>

          {/* Link Button Section */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Link Button (Optional)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="link">Link URL</Label>
                <Input
                  id="link"
                  value={formData.link}
                  onChange={(e) => setFormData((prev) => ({ ...prev, link: e.target.value }))}
                  placeholder="https://..."
                  data-testid="news-link-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="linkText">Button Text</Label>
                <Input
                  id="linkText"
                  value={formData.linkText}
                  onChange={(e) => setFormData((prev) => ({ ...prev, linkText: e.target.value }))}
                  placeholder="e.g., Learn More"
                  data-testid="news-link-text-input"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Add a link to display a button on the news article that links to an external page.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminNews;
